<template>
  <ModalShell :visible="visible" :z-index="2000" @close="$emit('close')">
    <ModalPanel width="380px" background="var(--bg-card)">
      <div class="replace-slot-header">
        <span>{{ t('keyboard_preset_save_btn') }}</span>
        <div class="mgx_close_btn" @click="$emit('close')">
          <SvgAsset :content="windowCloseSvg" />
        </div>
      </div>
      <div class="replace-slot-body">
        <p class="replace-slot-desc">{{ t('replace_slot_desc') }}</p>
        <div class="replace-slot-options">
          <label v-for="slot in 4" :key="slot" class="replace-slot-option" :class="{ selected: selected === slot - 1 }" @click="selected = slot - 1">
            <div class="slot-radio" :class="{ active: selected === slot - 1 }"></div>
            <div class="slot-info">
              <span class="slot-label">{{ slotLabel(slot - 1) }}</span>
              <span class="slot-current-name">{{ getSlotConfigName(slot - 1) }}</span>
            </div>
          </label>
        </div>
        <p class="replace-slot-warning">{{ t('replace_slot_warning') }}</p>
      </div>
      <div class="replace-slot-footer">
        <div class="mgx_action_btn" @click="$emit('close')">{{ t('cancel') }}</div>
        <div class="mgx_action_btn primary" :class="{ disabled: selected < 0 }" @click="selected >= 0 && confirm()">{{ t('replace_slot_confirm') }}</div>
      </div>
    </ModalPanel>
  </ModalShell>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { configSet } from '@/service/driver/config_set'
import i18n from '@/languages/i18n'
import ModalShell from '../common/ModalShell.vue'
import ModalPanel from '../common/ModalPanel.vue'
import SvgAsset from '../common/SvgAsset.vue'
import windowCloseSvg from '@/assets/inline-svg/window-close.svg?raw'

const { t } = i18n.global

const props = defineProps<{ visible: boolean }>()

const emit = defineEmits<{
  close: []
  confirm: [slotIndex: number]
}>()

const selected = ref(-1)

watch(
  () => props.visible,
  (v) => {
    if (v) selected.value = -1
  },
)

const slotLabel = (slot: number) => `${t('onboard_config_slot_prefix')} ${slot + 1}`

const getSlotConfigName = (slot: number) => {
  const item = configSet.activeConfigs.find((c) => c.OnboardSlot === slot)
  return item?.Name ?? ''
}

const confirm = () => {
  if (selected.value < 0) return
  emit('confirm', selected.value)
}
</script>

<style lang="less" scoped>
.replace-slot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-dark);
}

.replace-slot-body {
  padding: 16px 24px;

  .replace-slot-desc {
    color: var(--color-text-hint);
    font-size: 13px;
    margin: 0 0 16px;
    line-height: 1.5;
  }

  .replace-slot-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .replace-slot-option {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: var(--radius-m);
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.03);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      border-color: rgba(0, 0, 0, 0.12);
    }

    &.selected {
      background: rgba(var(--color-brand-1-rgb), 0.08);
      border-color: rgba(var(--color-brand-1-rgb), 0.3);
    }

    .slot-radio {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 2px solid rgba(0, 0, 0, 0.2);
      flex-shrink: 0;
      transition: all 0.2s ease;
      position: relative;

      &.active {
        border-color: var(--brand-color-pure);

        &::after {
          content: '';
          position: absolute;
          inset: 3px;
          border-radius: 50%;
          background: var(--brand-color-pure);
        }
      }
    }

    .slot-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;

      .slot-label {
        color: var(--color-dark);
        font-size: 13px;
        font-weight: 500;
      }

      .slot-current-name {
        color: var(--color-text-hint);
        font-size: 11px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .replace-slot-warning {
    color: #f59e0b;
    font-size: 12px;
    margin: 12px 0 0;
    line-height: 1.4;
  }
}

.replace-slot-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 24px 20px;

  .mgx_action_btn.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
}
</style>
